import React, { useEffect, useRef, useState, useMemo } from 'react';
import {
  MaterialReactTable,
  useMaterialReactTable,
} from 'material-react-table';
import styles from './MTable.module.scss';
import { Text } from '../typography';
import { useQueryParams } from '../../hooks/misc';
import {
  companyWideTables,
  saveTableSettings,
  useTableURLParams,
} from './table';
import { useTranslation } from 'react-i18next';
import { keys } from '../../utilities/translator/translation_keys';
import { tableOptions, tableStyle, FilterChip } from './MTableComponents';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { useURLParams } from '../../providers/URLParamProvider';
import { useWorkspace } from '../../providers/WorkspaceProvider';
import { Menu, MenuItem } from '@mui/material';
import { TableTabs } from './MTableComponents';
import classNames from 'classnames';
import TextWithIcon from '../typography/TextWithIcon';
import { useLocation, useNavigate } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import Icon from '../Icon';
import { customColors } from '../../utilities';
import { showToast } from '../../graphql/cache/modal';
import { Form } from 'react-bootstrap';
import { useOnlineStatus } from '../../hooks/offline-hooks/offline-misc';

const DEFAULT_TABLE_SETTINGS = {
  sorting: [{ id: 'dateCreated', desc: true }],
  pagination: { pageIndex: 0, pageSize: 10 },
  filters: [],
  columnVisibility: {},
  columnSizing: {},
  columnOrder: [],
  allWorkspaces: false,
};

export default function MTTable({
  data = [],
  columns = [],
  header,
  rowCount,
  isLoading,
  customToolbar = null,
  onRowClick = () => {},
  rightClickNavigation = null,
  onClearAllFilters = () => {},
  renderDetailPanel = undefined,
  enableExpanding = false,
  enableExpandAll = false,
  expanded = {},
  onExpandedChange = undefined,
  rightToolbar = null,
  className,
  dataCy,
  defaultSort = { id: 'dateCreated', desc: true },
  enableRowActions = false,
  rowActions = () => {},
  refetch = null,
  enableRowSelection = false,
  rowSelection = {},
  setRowSelection = () => {},
  initialState = {},
  listName = '',
  showBackButton,
  onBackClick = () => {},
  linkText = '',
  showAllWorkspacesCheck = false,
  noMargin,
  grouping = null,
  getSubRows = undefined,
  getRowCanExpand = undefined,
  muiExpandButtonProps = undefined,
  pagination: controlledPagination = undefined,
  onPaginationChange: controlledOnPaginationChange = undefined,
}) {
  const { getParam, deleteParam, upsertParam } = useQueryParams();
  const { tableFiltersToURL } = useTableURLParams();
  const {
    tableFilters,
    tablePageSize,
    tablePageIndex,
    cachedTableSettings,
    segment,
  } = useURLParams();
  const location = useLocation();
  const hasInitialized = useRef({});
  const tabParam = new URLSearchParams(location.search).get('tab') || segment;
  const { t } = useTranslation();
  const { workspaceId } = useWorkspace();
  const [contextMenu, setContextMenu] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);
  const [columnOrder, setColumnOrder] = useState(
    cachedTableSettings?.columnOrder ??
      columns.map((c) => c?.id ?? c.accessorKey),
  );
  const online = useOnlineStatus();
  const [sorting, setSorting] = useState(
    cachedTableSettings?.sorting || [defaultSort],
  );
  const [paginationState, setPaginationState] = useState(
    cachedTableSettings?.pagination ?? {
      pageIndex: parseInt(tablePageIndex, 10) || 0,
      pageSize: parseInt(tablePageSize, 10) || 10,
    },
  );
  const isPaginationControlled = controlledPagination !== undefined;
  const pagination = isPaginationControlled
    ? controlledPagination
    : paginationState;
  const setPagination = (updater) => {
    const next = typeof updater === 'function' ? updater(pagination) : updater;
    if (isPaginationControlled) {
      controlledOnPaginationChange?.(next);
    } else {
      setPaginationState(next);
    }
  };

  const [filters, setFilters] = useState(
    cachedTableSettings?.filters ?? tableFilters ?? [],
  );
  const [allWorkspaces, setAllWorkspaces] = useState(
    cachedTableSettings?.allWorkspaces,
  );
  const navigate = useNavigate();
  const [columnVisibility, setColumnVisibility] = useState(
    cachedTableSettings?.columnVisibility ?? {},
  );
  const [columnSizing, setColumnSizing] = useState(
    cachedTableSettings?.columnSizing ?? {},
  );
  const checkInId = getParam('id');
  useEffect(() => {
    if (segment === 'checkins' && checkInId) {
      setFilters((prev) => [...prev, { value: [checkInId], id: 'id' }]);
    }
    //eslint-disable-next-line
  }, [segment, checkInId]);

  useEffect(() => {
    if (!segment || hasInitialized.current[segment]) return;
    if (cachedTableSettings) {
      setSorting(cachedTableSettings.sorting ?? [defaultSort]);
      setPagination(
        cachedTableSettings.pagination ?? { pageIndex: 0, pageSize: 10 },
      );
      setFilters(cachedTableSettings.filters ?? []);
      setColumnVisibility(cachedTableSettings.columnVisibility ?? {});
      setColumnSizing(cachedTableSettings.columnSizing ?? {});
      setColumnOrder(
        cachedTableSettings.columnOrder ??
          columns.map((c) => c?.id ?? c.accessorKey),
      );
      setAllWorkspaces(cachedTableSettings?.allWorkspaces);
    } else {
      setSorting([defaultSort]);
      setPagination({ pageIndex: 0, pageSize: 10 });
      setFilters([]);
      setColumnVisibility({});
      setColumnSizing({});
      setColumnOrder(columns.map((c) => c?.id ?? c.accessorKey));
    }
    hasInitialized.current[segment] = true;
    // eslint-disable-next-line
  }, [segment]);

  useEffect(() => {
    setFilters(cachedTableSettings.filters ?? filters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabParam, pagination, cachedTableSettings?.filterURL]);

  const url = window.location.href;
  useEffect(() => {
    const allWorkspaceParam = getParam('allWorkspaces');
    saveTableSettings(
      segment,
      {
        sorting,
        pagination,
        filters,
        columnVisibility,
        columnSizing,
        columnOrder,
        filterURL: url.includes('?') ? url.split('?')[1] : '',
        allWorkspaces:
          companyWideTables.includes(segment) || allWorkspaceParam === 'TRUE'
            ? true
            : false,
      },
      tabParam,
    );
    // eslint-disable-next-line
  }, [
    segment,
    sorting,
    pagination,
    filters,
    columnVisibility,
    columnSizing,
    columnOrder,
    url,
  ]);
  useEffect(() => {
    tableFiltersToURL(filters, pagination, sorting, allWorkspaces);
    // eslint-disable-next-line
  }, [pagination, sorting, filters]);

  const handleRightClick = (event, row) => {
    setContextMenu(
      contextMenu === null
        ? { mouseX: event.clientX - 2, mouseY: event.clientY - 4 }
        : null,
    );
    setSelectedRow(row);
  };

  const openInNewWindow = () => {
    if (
      selectedRow &&
      (selectedRow?.original.__typename === 'Incident' ||
        selectedRow?.original.__typename === 'Timecard' ||
        selectedRow?.original.__typename === 'Expense')
    ) {
      window.open(`${rightClickNavigation}/${selectedRow.original.id}`);
    } else if (selectedRow) {
      window.open(
        `/workspace/${
          selectedRow.original.workspaceId || workspaceId
        }/${rightClickNavigation}/${selectedRow.original.id}`,
      );
    }
    setContextMenu(null);
  };
  const isDefaultSettings = useMemo(() => {
    const defaultOrder = columns.map((c) => c?.id ?? c.accessorKey);
    const orderIsDefault =
      !columnOrder ||
      (Array.isArray(columnOrder) &&
        columnOrder.length === defaultOrder.length &&
        columnOrder.every((val, i) => val === defaultOrder[i]));
    const visibilityIsDefault =
      !columnVisibility || Object.keys(columnVisibility).length === 0;
    const sizingIsDefault =
      !columnSizing || Object.keys(columnSizing).length === 0;
    const filtersIsDefault = !filters || filters.length === 0;
    const sortingIsDefault =
      !sorting ||
      sorting.length === 0 ||
      (sorting.length === 1 &&
        sorting[0].id === 'dateCreated' &&
        sorting[0].desc === true);
    const paginationIsDefault =
      !pagination || (pagination.pageIndex === 0 && pagination.pageSize === 10);

    return (
      orderIsDefault &&
      visibilityIsDefault &&
      sizingIsDefault &&
      filtersIsDefault &&
      sortingIsDefault &&
      paginationIsDefault
    );
  }, [
    columns,
    columnOrder,
    columnSizing,
    pagination,
    columnVisibility,
    filters,
    sorting,
  ]);
  const table = useMaterialReactTable({
    data,
    columns,
    ...tableOptions,
    ...tableStyle,
    state: {
      pagination,
      columnFilters: filters,
      isLoading,
      sorting,
      columnVisibility,
      columnSizing,
      columnOrder,
      rowSelection,
      grouping,
      ...(onExpandedChange !== undefined && { expanded }),
    },
    ...(onExpandedChange !== undefined && { onExpandedChange }),
    initialState: {
      ...initialState,
      pagination,
      columnFilters: filters,
      density: 'compact',
      showColumnFilters: true,
      columnVisibility: columnVisibility,
      columnSizing: columnSizing,
      columnOrder: columnOrder,
    },
    rowCount,
    enableExpandAll,
    enableExpanding,
    enableGrouping: !!grouping,
    ...(getSubRows && { getSubRows }),
    ...(getRowCanExpand && { getRowCanExpand }),
    ...(muiExpandButtonProps && { muiExpandButtonProps }),
    positionToolbarAlertBanner: 'none',
    enableRowSelection,
    enableRowVirtualization: true,
    getRowId: (row) => row.id,
    onRowSelectionChange: setRowSelection,
    onPaginationChange: setPagination,
    onColumnFiltersChange: setFilters,
    onSortingChange: setSorting,
    onColumnVisibilityChange: setColumnVisibility,
    onColumnSizingChange: setColumnSizing,
    onColumnOrderChange: setColumnOrder,
    renderDetailPanel,
    displayColumnDefOptions: {
      'mrt-row-expand': {
        header: '',
        size: 35,
        minSize: 35,
        maxSize: 35,
        muiTableHeadCellProps: { sx: { width: 35, p: 0 } },
        muiTableBodyCellProps: { sx: { width: 35, p: 0 } },
      },
    },
    enableRowActions,
    renderRowActionMenuItems: rowActions,
    enableColumnPinning: true,
    renderBottomToolbarCustomActions: () => (
      <TextWithIcon
        size="sm"
        color={!isDefaultSettings ? 'accentPrimary' : 'secondaryLight'}
        noMargin
        hover={!isDefaultSettings}
        leftIcon="refresh"
        iconProps={{
          color: !isDefaultSettings ? 'accentPrimary' : 'secondaryLight',
          fontSize: '1.2rem',
        }}
        onClick={() => {
          const currentTab = getParam('tab');
          saveTableSettings(segment, DEFAULT_TABLE_SETTINGS, currentTab);
          setSorting(DEFAULT_TABLE_SETTINGS.sorting);
          setPagination(DEFAULT_TABLE_SETTINGS.pagination);
          setFilters(DEFAULT_TABLE_SETTINGS.filters);
          deleteParam('parentId');
          setColumnVisibility({});
          setColumnSizing({});
          setColumnOrder(columns.map((c) => c?.id ?? c.accessorKey));
          navigate(`?${cachedTableSettings.filterURL}`, { replace: true });
        }}
      >
        Reset Table
      </TextWithIcon>
    ),
    muiTableBodyRowProps: ({ row }) => ({
      sx:
        row.depth > 0
          ? {
              backgroundColor: customColors.OFF_WHITE,
            }
          : {},
      onClick: () => {
        if (!online) return;
        onRowClick(row.original, row);
      },
      onContextMenu: (e) => {
        if (rightClickNavigation) {
          e.preventDefault();
          e.stopPropagation();
          handleRightClick(e, row);
        }
      },
    }),
    renderTopToolbarCustomActions: () => (
      <div className={styles.toolbarContainerParent}>
        <div className={styles.toolbarContainer}>
          <div className={styles.toolbar}>
            <Text
              noMargin
              hover={!!filters?.length}
              color={!!filters?.length ? 'accentPrimary' : 'secondaryLight'}
              onClick={() => {
                setFilters([]);
                onClearAllFilters();
                saveTableSettings(segment, DEFAULT_TABLE_SETTINGS, tabParam);
              }}
              disabled={!!!filters}
            >
              {t(keys.common.CLEAR_ALL_FILTERS).toUpperCase()}
            </Text>
            {showAllWorkspacesCheck && (
              <div className={styles.check}>
                <Form.Check
                  size={'small'}
                  checked={allWorkspaces}
                  onChange={(e) => {
                    setAllWorkspaces(e.target.checked);
                    if (e.target.checked) {
                      upsertParam('allWorkspaces', 'TRUE');
                    } else {
                      upsertParam('allWorkspaces', 'FALSE');
                    }
                  }}
                />
                <Text noMargin size="sm">
                  {t(keys.templates.ALL_WORKSPACES)}
                </Text>
              </div>
            )}
            {customToolbar}
          </div>
          {rightToolbar && (
            <TableTabs
              parent={rightToolbar}
              onChange={() => {
                setFilters([]);
                onClearAllFilters();
              }}
            />
          )}
          {!!refetch && (
            <Icon
              hover
              disabled={isLoading}
              onClick={() => {
                refetch().then(() =>
                  showToast({
                    title: 'Data Updated',
                    message: 'The latest data has been successfully loaded!',
                  }),
                );
              }}
              style={{ color: customColors.BLUE }}
            >
              refresh
            </Icon>
          )}
        </div>

        {showBackButton && (
          <div className={styles.toolbar}>
            <TextWithIcon
              leftIcon="arrow_left"
              noMargin
              size="sm"
              weight="semiBold"
              color="accentPrimary"
              hover
              onClick={() => onBackClick()}
            >
              Back
            </TextWithIcon>
            <Text size="sm" noMargin>
              {linkText}
            </Text>
          </div>
        )}
        {filters?.length > 0 && (
          <div className={styles.filterContainer}>
            {filters.map((filter) => (
              <FilterChip
                key={`filter-group-${uuidv4()}`}
                filter={filter}
                setFilters={setFilters}
                listName={listName}
              />
            ))}
          </div>
        )}
      </div>
    ),
  });

  return (
    <div
      data-cy={dataCy}
      className={classNames(
        `${styles.page} ${
          table.getState()?.isFullScreen ? styles.fullScreen : ''
        }`,
        className,
        noMargin && styles.noMargin,
      )}
    >
      <div className={styles.header}>{header}</div>
      <LocalizationProvider dateAdapter={AdapterMoment}>
        <MaterialReactTable table={table} />
        <Menu
          open={contextMenu !== null}
          onClose={() => setContextMenu(null)}
          anchorReference="anchorPosition"
          anchorPosition={
            contextMenu !== null
              ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
              : undefined
          }
        >
          <MenuItem onClick={() => openInNewWindow()}>
            {t(keys.action.OPEN_IN_NEW_WINDOW)}
          </MenuItem>
        </Menu>
      </LocalizationProvider>
    </div>
  );
}
